import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import { accountsAPI } from '../services/api';
import Navbar from '../components/Navbar';

const accountTypeColors = { checking: '#1976d2', savings: '#388e3c', credit: '#d32f2f' };
const categoryColors = { personal: '#9c27b0', joint: '#f57c00', work: '#455a64' };

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', institution: '', account_type: 'checking', category: 'personal' });

  const loadData = useCallback(async () => {
    try {
      const acctRes = await accountsAPI.list();
      setAccounts(acctRes.data);
    } catch (err) {
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    try {
      await accountsAPI.create(form);
      setDialogOpen(false);
      setForm({ name: '', institution: '', account_type: 'checking', category: 'personal' });
      loadData();
    } catch (err) {
      setError('Failed to create account');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account and all its transactions?')) return;
    try {
      await accountsAPI.delete(id);
      loadData();
    } catch (err) {
      setError('Failed to delete account');
    }
  };


  return (
    <>
      <Navbar />
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight={700}>Accounts</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Account
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <Box textAlign="center" py={4}><CircularProgress /></Box>}

        <Grid container spacing={2}>
          {accounts.map((acc) => (
            <Grid item xs={12} sm={6} md={4} key={acc.id}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Typography variant="h6" fontWeight={600}>{acc.name}</Typography>
                    <Chip label={acc.account_type} size="small" sx={{ bgcolor: accountTypeColors[acc.account_type], color: 'white' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {acc.institution || 'Manual Account'}
                  </Typography>
                  <Chip label={acc.category} size="small" variant="outlined" sx={{ mt: 1, borderColor: categoryColors[acc.category], color: categoryColors[acc.category] }} />
                  
                </CardContent>
                <CardActions>
                  <Button size="small" color="error" onClick={() => handleDelete(acc.id)}>Delete</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
          {!loading && accounts.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" textAlign="center" py={4}>
                No accounts yet. Add checking, savings, or credit accounts.
              </Typography>
            </Grid>
          )}
        </Grid>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Account</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField label="Account Name" fullWidth value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Institution (optional)" fullWidth value={form.institution}
                  onChange={(e) => setForm({ ...form, institution: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={form.account_type} label="Type"
                    onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
                    <MenuItem value="checking">Checking</MenuItem>
                    <MenuItem value="savings">Savings</MenuItem>
                    <MenuItem value="credit">Credit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select value={form.category} label="Category"
                    onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <MenuItem value="personal">Personal</MenuItem>
                    <MenuItem value="joint">Joint</MenuItem>
                    <MenuItem value="work">Work</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate}>Create</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
